import { useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "@follosoft/react-components";

import type { ContractContactResource } from "../types";
import type { OccupantFormValues } from "../hooks/use-occupant-form-schema";
import type { OccupantPayload } from "../utils/occupant-payload";

import { useContractContactConstants } from "../hooks/use-contract-contact-constants";
import { occupantPayload, occupantSubscriptions } from "../utils/occupant-payload";

import { CONTACTS_ENDPOINT, CONTRACT_CONTACTS_ENDPOINT, RESOURCE_TYPE } from "./endpoints";
import { contractContactsQueryKey } from "./query-keys";

/** One subscription as the resource stores it: the alert type, and the channel
 * it delivers to named by value. */
interface ContractContactWrite {
  alertTypeId: number;
  destination: string;
}

/**
 * Writes the person and returns their id. Their destinations go with them —
 * that is what puts the channel values in the caller's hands for the
 * subscriptions that follow.
 */
const storeContact = async (contact: OccupantPayload["contact"]): Promise<string> => {
  const { data } = await axiosInstance().post(CONTACTS_ENDPOINT, {
    data: { type: "contacts", attributes: contact },
  });

  const contactId = data?.data?.id;

  if (!contactId) {
    throw new Error("The contact was created but the response carried no id to attach its alerts to.");
  }

  return String(contactId);
};

/**
 * Saves the Create / Edit contact drawer against the plain `contract-contacts`
 * resource: one request per row, since there is no atomic-operations route to
 * batch them behind.
 *
 * The contract's subscriptions are reconciled against what is now ticked — one
 * row per (destination, alert type), stored where new and destroyed where
 * cleared. Rows that already exist are left alone, so their opt-in state
 * survives an unrelated edit.
 *
 * Every row must name an existing contact — `contact_id` is required and checked
 * against the contract — so a new person is created first and their id carried
 * into the rows. That ordering is also what lets the rows name their channel by
 * value: the destinations went out with the contact a moment earlier, so the
 * client has the values in hand and never saw the ids.
 */
export const useSaveOccupantService = (contractId: string | number, existing: ContractContactResource[] = []) => {
  const queryClient = useQueryClient();
  const { requireAlertTypeId } = useContractContactConstants();

  return useMutation({
    mutationFn: async (values: OccupantFormValues) => {
      const wanted = occupantSubscriptions(values);

      // Keyed by destination *and* alert type: the same number can carry both a
      // call and an SMS row, and two numbers can both carry SMS.
      const key = (alertType: string, destination: string) => `${alertType}|${destination}`;
      const existingRows = new Map(
        existing.map((contractContact) => [
          key(
            String(contractContact.attributes.alert_type),
            String(contractContact.attributes.alert_destination ?? ""),
          ),
          String(contractContact.id),
        ]),
      );

      const wantedKeys = new Set(wanted.map((subscription) => key(subscription.alertType, subscription.destination)));

      // Rows the form no longer asks for, by alert type. Retargeting a
      // destination — fixing a typo'd address, moving SMS to another number —
      // reads as one of these plus a wanted row on the same alert type, so it
      // is written as an update of that row rather than a destroy and a store:
      // the backend rejects a second row for the same (contact, alert type),
      // and reusing the row keeps its opt-in state.
      const reusable = new Map<string, string[]>();

      for (const [existingKey, contractContactId] of existingRows) {
        if (wantedKeys.has(existingKey)) {
          continue;
        }

        const [alertType] = existingKey.split("|");

        reusable.set(alertType, [...(reusable.get(alertType) ?? []), contractContactId]);
      }

      const retargets: { id: string; destination: string }[] = [];
      const additions: ContractContactWrite[] = [];

      for (const subscription of wanted) {
        if (existingRows.has(key(subscription.alertType, subscription.destination))) {
          continue;
        }

        const retargeted = reusable.get(subscription.alertType)?.shift();

        if (retargeted) {
          retargets.push({ id: retargeted, destination: subscription.destination });

          continue;
        }

        additions.push({
          alertTypeId: requireAlertTypeId(subscription.alertType),
          destination: subscription.destination,
        });
      }

      // A new person is written first: the rows can only reference a contact
      // that exists, and sending the destinations here is what lets those rows
      // name their channel by value afterwards.
      const contactId = values.contactId || (await storeContact(occupantPayload(values).contact));

      const storeRow = ({ alertTypeId, destination }: ContractContactWrite) =>
        axiosInstance().post(CONTRACT_CONTACTS_ENDPOINT, {
          data: {
            type: RESOURCE_TYPE,
            attributes: { alert_type_id: alertTypeId, destination },
            relationships: {
              contract: { data: { type: "contracts", id: String(contractId) } },
              contact: { data: { type: "contacts", id: String(contactId) } },
            },
          },
        });

      await Promise.all([
        ...additions.map(storeRow),
        ...retargets.map(({ id, destination }) =>
          axiosInstance().patch(`${CONTRACT_CONTACTS_ENDPOINT}/${id}`, {
            data: { type: RESOURCE_TYPE, id: String(id), attributes: { destination } },
          }),
        ),
      ]);

      // Whatever is left over was genuinely dropped, not retargeted.
      const removals = [...reusable.values()].flat();

      await Promise.all(
        removals.map((id) => axiosInstance().delete(`${CONTRACT_CONTACTS_ENDPOINT}/${id}`)),
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contractContactsQueryKey(String(contractId)) });
    },
  });
};
